/**
 * Accept AI plan items onto itinerary days / options — mirrors iOS CityPlanningView save paths.
 * Dates/times are UTC wall-clock civil digits (YYYY-MM-DD + HH:mm), never device-local TZ.
 */

import type {
  ItineraryDay,
  ItineraryItem,
  ItineraryItemType,
  Trip,
  TripCityOption,
  TripUserPlanSelection,
} from '../types/client';
import type {
  AggregatedPlanItem,
  PlanItineraryDayBlock,
  PlanTimeSlot,
} from './aggregatedPlanData';
import { normalizePlanTitle } from './aggregatedPlanData';
import { defaultStartHour } from './cityPlanningSlots';
import { parseItemTimeToHHmm } from './itemTime';
import { hmFromUnknown, ymdFromUnknown } from './wallClock';

function newId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `sel-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

/** Normalize to civil YYYY-MM-DD for itinerary day keys (iOS wall-clock). */
export function wallClockDateYmd(raw: string | null | undefined): string {
  return ymdFromUnknown(raw) ?? String(raw ?? '').trim().slice(0, 10);
}

/** Normalize to civil HH:mm for itinerary item.time (iOS wall-clock). */
export function wallClockTimeHm(raw: string | null | undefined): string {
  return parseItemTimeToHHmm(raw) ?? hmFromUnknown(raw) ?? '09:00';
}

export function resolveItemType(itemType: string | null | undefined): ItineraryItemType {
  const t = String(itemType ?? '').toLowerCase();
  if (t === 'meal' || t === 'food' || t === 'restaurant') return 'meal';
  if (t === 'accommodation' || t === 'hotel') return 'accommodation';
  if (t === 'transport') return 'transport';
  return 'activity';
}

export function minutesBetweenHm(startHm: string, endHm: string): number | undefined {
  const aHm = parseItemTimeToHHmm(startHm) ?? hmFromUnknown(startHm);
  const bHm = parseItemTimeToHHmm(endHm) ?? hmFromUnknown(endHm);
  if (!aHm || !bHm) return undefined;
  const parse = (hm: string): number | null => {
    const [h, m] = hm.split(':').map((p) => Number.parseInt(p, 10));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };
  const a = parse(aHm);
  const b = parse(bHm);
  if (a == null || b == null || b <= a) return undefined;
  return b - a;
}

export function defaultTimesForSlot(slot: PlanTimeSlot | string): {
  startTime: string;
  endTime: string;
} {
  const typed = (slot as PlanTimeSlot) || 'morning';
  const hour = defaultStartHour(
    ['breakfast', 'morning', 'lunch', 'afternoon', 'dinner', 'evening', 'night'].includes(
      typed
    )
      ? typed
      : 'morning'
  );
  const startTime = `${pad2(hour)}:00`;
  const endTime = `${pad2(Math.min(hour + 2, 23))}:00`;
  return { startTime, endTime };
}

export function buildPlanItineraryItem(params: {
  title: string;
  itemType?: string | null;
  dateYmd: string;
  startTime: string;
  endTime?: string | null;
  description?: string | null;
  location?: string | null;
  url?: string | null;
  reservationRequirement?: string | null;
  confirmationNumber?: string | null;
  suggestedDurationMinutes?: number | null;
  geocode?: { latitude: number; longitude: number } | null;
}): Omit<ItineraryItem, 'id'> {
  const type = resolveItemType(params.itemType);
  const startHm = wallClockTimeHm(params.startTime);
  const endHm = params.endTime ? wallClockTimeHm(params.endTime) : undefined;
  const durationMinutes =
    params.suggestedDurationMinutes ??
    (endHm ? minutesBetweenHm(startHm, endHm) : undefined);
  const locationAddress = params.location?.trim();
  return {
    title: params.title,
    type,
    time: startHm,
    durationMinutes,
    notes: params.description?.trim() || undefined,
    url: params.url?.trim() || undefined,
    reservationRequirement: params.reservationRequirement?.trim() || undefined,
    confirmationNumber: params.confirmationNumber?.trim() || undefined,
    location:
      locationAddress || params.geocode
        ? {
            address: locationAddress || params.title,
            latitude: params.geocode?.latitude,
            longitude: params.geocode?.longitude,
          }
        : undefined,
  };
}

export function buildPlanItemFromAggregated(
  item: AggregatedPlanItem,
  dateYmd: string,
  startTime?: string,
  endTime?: string
): Omit<ItineraryItem, 'id'> {
  const defaults = defaultTimesForSlot(item.timeSlot as PlanTimeSlot);
  return buildPlanItineraryItem({
    title: item.title,
    itemType: item.itemType,
    dateYmd,
    startTime: startTime || item.startTime || defaults.startTime,
    endTime: endTime || item.endTime || defaults.endTime,
    description: item.description,
    location: item.location,
    url: item.url,
    reservationRequirement: item.reservationRequirement,
    suggestedDurationMinutes: item.suggestedDurationMinutes,
    geocode: item.geocode,
  });
}

export function buildPlanItemFromBlock(
  block: PlanItineraryDayBlock,
  dateYmd: string
): Omit<ItineraryItem, 'id'> | null {
  const title = block.title?.trim();
  if (!title) return null;
  const defaults = defaultTimesForSlot((block.timeSlot as PlanTimeSlot) || 'morning');
  return buildPlanItineraryItem({
    title,
    itemType: block.itemType,
    dateYmd,
    startTime: block.startTime || defaults.startTime,
    endTime: block.endTime || defaults.endTime,
    description: block.description,
    location: block.location,
    url: block.url,
    reservationRequirement: block.reservationRequirement,
  });
}

/** True when [start, end) overlaps an existing timed item on the same day. */
export function hasScheduleOverlap(
  items: ItineraryItem[],
  startHm: string,
  endHm: string
): boolean {
  const toMins = (hm: string): number | null => {
    const normalized = parseItemTimeToHHmm(hm) ?? hmFromUnknown(hm);
    if (!normalized) return null;
    const [h, m] = normalized.split(':').map((p) => Number.parseInt(p, 10));
    if (!Number.isFinite(h) || !Number.isFinite(m)) return null;
    return h * 60 + m;
  };
  const start = toMins(startHm);
  const end = toMins(endHm);
  if (start == null) return false;
  const endExclusive = end ?? start + 60;

  for (const item of items) {
    if (!item.time) continue;
    const itemStart = toMins(item.time);
    if (itemStart == null) continue;
    const itemEnd = itemStart + (item.durationMinutes ?? 60);
    if (start < itemEnd && endExclusive > itemStart) return true;
  }
  return false;
}

export function buildUserPlanSelection(params: {
  tripId: string;
  cityVisitId: string;
  itemTitle: string;
  normalizedTitle?: string;
  selectedSlot?: string;
  selectedDate?: string;
}): TripUserPlanSelection {
  return {
    id: newId(),
    tripId: params.tripId,
    cityVisitId: params.cityVisitId,
    itemTitle: params.itemTitle,
    normalizedTitle:
      params.normalizedTitle || normalizePlanTitle(params.itemTitle),
    selectedSlot: params.selectedSlot,
    selectedDate: params.selectedDate
      ? wallClockDateYmd(params.selectedDate)
      : undefined,
    createdAt: new Date().toISOString(),
  };
}

export function selectedNormalizedTitles(
  trip: Trip,
  cityVisitId: string
): Set<string> {
  const set = new Set<string>();
  for (const sel of trip.userPlanSelections ?? []) {
    if (sel.cityVisitId !== cityVisitId) continue;
    const n = (sel.normalizedTitle || normalizePlanTitle(sel.itemTitle)).trim();
    if (n) set.add(n);
  }
  return set;
}

export function isTitleSelected(
  trip: Trip,
  cityVisitId: string,
  title: string,
  normalizedTitle?: string
): boolean {
  const selected = selectedNormalizedTitles(trip, cityVisitId);
  const key = (normalizedTitle || normalizePlanTitle(title)).trim();
  return selected.has(key);
}

/**
 * Append an itinerary item to a day (create day if needed) and record selection.
 */
export function applyAddPlanItemToTrip(
  trip: Trip,
  params: {
    cityVisitId: string;
    dateYmd: string;
    item: Omit<ItineraryItem, 'id'>;
    selection: Omit<TripUserPlanSelection, 'id'> & { id?: string };
  }
): Partial<Trip> {
  const dateYmd = wallClockDateYmd(params.dateYmd);
  const days: ItineraryDay[] = [...(trip.itineraryDays ?? [])];
  const dayIndex = days.findIndex(
    (d) => d.date === dateYmd && (d.cityId === params.cityVisitId || !d.cityId)
  );
  const normalizedTime = params.item.time
    ? wallClockTimeHm(params.item.time)
    : params.item.time;
  const newItem: ItineraryItem = {
    ...params.item,
    id: newId(),
    time: normalizedTime,
  };
  if (dayIndex >= 0) {
    days[dayIndex] = {
      ...days[dayIndex],
      cityId: days[dayIndex].cityId || params.cityVisitId,
      items: [...days[dayIndex].items, newItem],
      updatedAt: new Date().toISOString(),
    };
  } else {
    days.push({
      date: dateYmd,
      cityId: params.cityVisitId,
      items: [newItem],
      updatedAt: new Date().toISOString(),
    });
  }

  const selection: TripUserPlanSelection = {
    id: params.selection.id || newId(),
    tripId: params.selection.tripId,
    cityVisitId: params.selection.cityVisitId,
    itemTitle: params.selection.itemTitle,
    normalizedTitle: params.selection.normalizedTitle,
    selectedSlot: params.selection.selectedSlot,
    selectedDate: params.selection.selectedDate
      ? wallClockDateYmd(params.selection.selectedDate)
      : dateYmd,
    createdAt: params.selection.createdAt ?? new Date().toISOString(),
  };

  return {
    itineraryDays: days,
    userPlanSelections: [...(trip.userPlanSelections ?? []), selection],
  };
}

export function buildCityOptionFromPlan(params: {
  title: string;
  description?: string | null;
  url?: string | null;
  address?: string | null;
  order: number;
}): TripCityOption {
  return {
    id: newId(),
    title: params.title,
    description: params.description?.trim() || undefined,
    url: params.url?.trim() || undefined,
    address: params.address?.trim() || undefined,
    completed: false,
    order: params.order,
  };
}

export function applyAddPlanItemAsOptional(
  trip: Trip,
  params: {
    cityVisitId: string;
    option: TripCityOption;
    selection: TripUserPlanSelection;
  }
): Partial<Trip> {
  const existing = trip.cityOptions?.[params.cityVisitId] ?? [];
  return {
    cityOptions: {
      ...(trip.cityOptions ?? {}),
      [params.cityVisitId]: [...existing, params.option],
    },
    userPlanSelections: [...(trip.userPlanSelections ?? []), params.selection],
  };
}

export interface SaveDayBlocksResult {
  patch: Partial<Trip>;
  savedCount: number;
  conflictCount: number;
  skippedCount: number;
}

/**
 * Bulk-accept itinerary day blocks, skipping already-selected titles and
 * schedule overlaps (mirrors iOS handleSaveItineraryDay).
 */
export function applySaveDayBlocks(
  trip: Trip,
  params: {
    cityVisitId: string;
    dateYmd: string;
    blocks: PlanItineraryDayBlock[];
  }
): Partial<Trip> {
  return applySaveDayBlocksWithStats(trip, params).patch;
}

export function applySaveDayBlocksWithStats(
  trip: Trip,
  params: {
    cityVisitId: string;
    dateYmd: string;
    blocks: PlanItineraryDayBlock[];
  }
): SaveDayBlocksResult {
  const dateYmd = wallClockDateYmd(params.dateYmd);
  let next: Trip = { ...trip };
  let savedCount = 0;
  let conflictCount = 0;
  let skippedCount = 0;

  const daySnapshot =
    next.itineraryDays?.find(
      (d) =>
        d.date === dateYmd &&
        (d.cityId === params.cityVisitId || !d.cityId)
    )?.items ?? [];

  for (const block of params.blocks) {
    const title = block.title?.trim();
    if (!title) {
      skippedCount += 1;
      continue;
    }
    const normalized = block.normalizedTitle || normalizePlanTitle(title);
    if (isTitleSelected(next, params.cityVisitId, title, normalized)) {
      skippedCount += 1;
      continue;
    }
    const item = buildPlanItemFromBlock(block, dateYmd);
    if (!item) {
      skippedCount += 1;
      continue;
    }
    const startHm = item.time || '';
    const endHm = item.durationMinutes
      ? (() => {
          const m = /^(\d{1,2}):(\d{2})$/.exec(startHm);
          if (!m) return '';
          const total =
            Number(m[1]) * 60 + Number(m[2]) + (item.durationMinutes ?? 60);
          return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
        })()
      : '';
    if (startHm && hasScheduleOverlap(daySnapshot, startHm, endHm || startHm)) {
      conflictCount += 1;
      continue;
    }
    const selection = buildUserPlanSelection({
      tripId: trip.id,
      cityVisitId: params.cityVisitId,
      itemTitle: title,
      normalizedTitle: normalized,
      selectedSlot: block.timeSlot ?? undefined,
      selectedDate: dateYmd,
    });
    const patch = applyAddPlanItemToTrip(next, {
      cityVisitId: params.cityVisitId,
      dateYmd,
      item,
      selection,
    });
    next = { ...next, ...patch };
    savedCount += 1;
  }
  return {
    patch: {
      itineraryDays: next.itineraryDays,
      userPlanSelections: next.userPlanSelections,
    },
    savedCount,
    conflictCount,
    skippedCount,
  };
}
